/**
 * LeadsDashboard - Vista simple de solicitudes de contacto
 * Solo muestra las solicitudes recibidas, sin gestión de estados
 */

import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  useLeads,
  Lead,
  formatearFechaLead,
  getTipoContactoLabel
} from '../hooks/useLeads'

interface LeadsDashboardProps {
  asesorId: string
  variant?: 'widget' | 'full'
  maxItems?: number
}

// Tipos de filtro
type TipoContactoFiltro = 'todos' | 'informacion' | 'visita' | 'oferta' | 'otro'
type PeriodoFiltro = 'todos' | 'hoy' | 'semana' | 'mes'

export default function LeadsDashboard({
  asesorId,
  variant = 'widget',
  maxItems = 10
}: LeadsDashboardProps) {
  const { leads, loading, error, totalLeads, refetch } = useLeads({
    asesorId,
    limitResults: variant === 'widget' ? 5 : 100 // Cargar más para filtrar en cliente
  })

  const [expandedLead, setExpandedLead] = useState<string | null>(null)

  // Estados de filtros (solo para vista full)
  const [filtroTipo, setFiltroTipo] = useState<TipoContactoFiltro>('todos')
  const [filtroPeriodo, setFiltroPeriodo] = useState<PeriodoFiltro>('todos')
  const [busqueda, setBusqueda] = useState('')

  // Filtrar leads
  const leadsFiltrados = useMemo(() => {
    if (variant === 'widget') return leads.slice(0, 5)

    return leads.filter(lead => {
      // Filtro por tipo de contacto
      if (filtroTipo !== 'todos' && lead.tipoContacto !== filtroTipo) {
        return false
      }

      // Filtro por período
      if (filtroPeriodo !== 'todos' && lead.fechaCreacion?.toDate) {
        const fechaLead = lead.fechaCreacion.toDate()
        const ahora = new Date()

        if (filtroPeriodo === 'hoy') {
          const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
          if (fechaLead < inicioHoy) return false
        } else if (filtroPeriodo === 'semana') {
          const hace7dias = new Date(ahora.getTime() - 7 * 24 * 60 * 60 * 1000)
          if (fechaLead < hace7dias) return false
        } else if (filtroPeriodo === 'mes') {
          const hace30dias = new Date(ahora.getTime() - 30 * 24 * 60 * 60 * 1000)
          if (fechaLead < hace30dias) return false
        }
      }

      // Filtro por búsqueda
      if (busqueda.trim()) {
        const searchLower = busqueda.toLowerCase()
        const matchNombre = lead.nombre?.toLowerCase().includes(searchLower)
        const matchPropiedad = lead.propertyName?.toLowerCase().includes(searchLower)
        const matchEmail = lead.email?.toLowerCase().includes(searchLower)
        if (!matchNombre && !matchPropiedad && !matchEmail) return false
      }

      return true
    }).slice(0, maxItems)
  }, [leads, filtroTipo, filtroPeriodo, busqueda, variant, maxItems])

  // Widget compacto para el dashboard
  if (variant === 'widget') {
    return (
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="fa-solid fa-envelope text-primary me-2"></i>
            Solicitudes Recibidas
            {totalLeads > 0 && (
              <span className="badge bg-primary ms-2">{totalLeads}</span>
            )}
          </h6>
          <Link to="/mis-leads" className="btn btn-sm btn-outline-primary">
            Ver todas
          </Link>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="small text-muted mt-2 mb-0">Cargando solicitudes...</p>
            </div>
          ) : error ? (
            <div className="text-center text-danger py-4">
              <i className="fa-solid fa-triangle-exclamation fa-2x mb-2 d-block"></i>
              <small>{error}</small>
              <button className="btn btn-sm btn-outline-primary d-block mx-auto mt-2" onClick={refetch}>
                Reintentar
              </button>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="fa-regular fa-envelope fa-2x mb-2 d-block"></i>
              <small>No tienes solicitudes</small>
            </div>
          ) : (
            <ul className="list-group list-group-flush">
              {leads.map(lead => (
                <li
                  key={lead.id}
                  className="list-group-item d-flex justify-content-between align-items-center py-3"
                >
                  <div>
                    <div className="fw-semibold">{lead.nombre}</div>
                    <small className="text-muted d-block">{lead.propertyName?.substring(0, 30)}...</small>
                    <span className="badge bg-light text-dark mt-1">
                      {getTipoContactoLabel(lead.tipoContacto)}
                    </span>
                  </div>
                  <small className="text-muted">{formatearFechaLead(lead.fechaCreacion)}</small>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    )
  }

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltroTipo('todos')
    setFiltroPeriodo('todos')
    setBusqueda('')
  }

  const hayFiltrosActivos = filtroTipo !== 'todos' || filtroPeriodo !== 'todos' || busqueda.trim() !== ''

  // Vista completa
  return (
    <div className="leads-list">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span className="text-muted">
            {leadsFiltrados.length} de {totalLeads} solicitud{totalLeads !== 1 ? 'es' : ''}
          </span>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={refetch}>
          <i className="fa-solid fa-refresh me-1"></i>
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            {/* Búsqueda */}
            <div className="col-md-4">
              <label className="form-label small text-muted">Buscar</label>
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="fa-solid fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre, email o propiedad..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
            </div>

            {/* Tipo de contacto */}
            <div className="col-md-3">
              <label className="form-label small text-muted">Tipo de solicitud</label>
              <select
                className="form-select"
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value as TipoContactoFiltro)}
              >
                <option value="todos">Todos los tipos</option>
                <option value="informacion">Información</option>
                <option value="visita">Agendar visita</option>
                <option value="oferta">Oferta</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            {/* Período */}
            <div className="col-md-3">
              <label className="form-label small text-muted">Período</label>
              <select
                className="form-select"
                value={filtroPeriodo}
                onChange={(e) => setFiltroPeriodo(e.target.value as PeriodoFiltro)}
              >
                <option value="todos">Todo el tiempo</option>
                <option value="hoy">Hoy</option>
                <option value="semana">Últimos 7 días</option>
                <option value="mes">Últimos 30 días</option>
              </select>
            </div>

            {/* Botón limpiar */}
            <div className="col-md-2">
              {hayFiltrosActivos && (
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={limpiarFiltros}
                >
                  <i className="fa-solid fa-times me-1"></i>
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fa-solid fa-triangle-exclamation me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-auto" onClick={refetch}>
            Reintentar
          </button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted mt-3">Cargando solicitudes...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-5">
          <i className="fa-regular fa-envelope fa-4x text-muted mb-3"></i>
          <h5 className="text-muted">Sin solicitudes</h5>
          <p className="text-muted">
            Las solicitudes de contacto de tus propiedades aparecerán aquí.
          </p>
        </div>
      ) : leadsFiltrados.length === 0 ? (
        <div className="text-center py-5">
          <i className="fa-solid fa-filter-circle-xmark fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">Sin resultados</h5>
          <p className="text-muted mb-3">
            No hay solicitudes que coincidan con los filtros seleccionados.
          </p>
          <button className="btn btn-outline-primary" onClick={limpiarFiltros}>
            <i className="fa-solid fa-times me-1"></i>
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="list-group">
          {leadsFiltrados.map(lead => (
            <div
              key={lead.id}
              className="list-group-item list-group-item-action"
            >
              {/* Header del lead */}
              <div
                className="d-flex justify-content-between align-items-start"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}
              >
                <div className="d-flex align-items-start">
                  <div className="me-3">
                    <span className="badge bg-primary rounded-circle p-2">
                      <i className="fa-solid fa-user"></i>
                    </span>
                  </div>
                  <div>
                    <h6 className="mb-1">{lead.nombre}</h6>
                    <p className="text-muted small mb-1">
                      <i className="fa-solid fa-home me-1"></i>
                      <Link
                        to={`/single-property-1/${lead.propertyId}`}
                        className="text-decoration-none"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.propertyName}
                      </Link>
                    </p>
                    <span className="badge bg-light text-dark">
                      {getTipoContactoLabel(lead.tipoContacto)}
                    </span>
                  </div>
                </div>
                <div className="text-end">
                  <small className="text-muted d-block">{formatearFechaLead(lead.fechaCreacion)}</small>
                  <i className={`fa-solid fa-chevron-${expandedLead === lead.id ? 'up' : 'down'} text-muted mt-2`}></i>
                </div>
              </div>

              {/* Detalles expandidos */}
              {expandedLead === lead.id && (
                <div className="mt-3 pt-3 border-top">
                  {/* Datos de contacto */}
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <div className="d-flex align-items-center">
                        <i className="fa-solid fa-envelope text-primary me-2"></i>
                        <a href={`mailto:${lead.email}`}>{lead.email}</a>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex align-items-center">
                        <i className="fa-solid fa-phone text-primary me-2"></i>
                        <a href={`tel:${lead.telefono}`}>{lead.telefono}</a>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <a
                        href={`https://wa.me/${lead.telefono.replace(/\D/g, '')}?text=Hola ${lead.nombre}, me contacto por tu interés en ${lead.propertyName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-success"
                      >
                        <i className="fa-brands fa-whatsapp me-1"></i>
                        WhatsApp
                      </a>
                    </div>
                  </div>

                  {/* Mensaje */}
                  <div className="bg-light rounded p-3">
                    <small className="text-muted d-block mb-1">Mensaje:</small>
                    <p className="mb-0">{lead.mensaje}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
